import { createSelector } from 'reselect'
import { Issuers } from 'src/enums'
import {
  CreditCardListModel,
  CreditCardListResponse,
  CreditCardType,
} from 'src/models/creditCard.models'
import {
  CreditCardPermissionsModel,
  CreditCardPermissionsResponse,
} from 'src/models/creditCardPermissions.models'
import { UserListModel } from 'src/models/user.models'
import { RootState } from 'src/redux/createRootReducer'

// pure data + models
export const selectCreditCardListResponsesFromState = (
  state: RootState,
): Record<string, CreditCardListResponse> =>
  state.data.creditCards.listResponses
export const selectCreditCardListModelsFromState = createSelector(
  [selectCreditCardListResponsesFromState],
  (creditCardListResponses) =>
    Object.values(creditCardListResponses).reduce<
      Record<CreditCardListResponse['id'], CreditCardListModel>
    >(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new CreditCardListModel(curr),
      }),
      {},
    ),
)

export const selectNonReadOnlyCreditCardListModelsFromState = createSelector(
  [selectCreditCardListModelsFromState],
  (creditCardListModels) =>
    Object.values(creditCardListModels).filter(
      (cc) => !cc.readOnly && cc.type !== CreditCardType.Delegate,
    ),
)

export const selectAmexCreditCardListModelsFromState = createSelector(
  [selectCreditCardListModelsFromState],
  (creditCardListModels) =>
    Object.values(creditCardListModels).filter(
      (cc) => cc.issuer === Issuers.Amex || cc.issuer === Issuers.AmexDemo,
    ),
)

export const selectCreditCardPermissionsResponsesFromState = (
  state: RootState,
): Record<string, CreditCardPermissionsResponse> =>
  state.data.creditCards.permissions
export const selectCreditCardPermissionsModelsFromState = createSelector(
  [selectCreditCardPermissionsResponsesFromState],
  (creditCardPermissions) =>
    Object.entries(creditCardPermissions).reduce(
      (acc, curr) => ({
        ...acc,
        [curr[0]]: new CreditCardPermissionsModel(curr[1]),
      }),
      {} as Record<string, CreditCardPermissionsModel>,
    ),
)

export const selectCreditCardStatisticsActiveVcnCountFromState = (
  state: RootState,
): Record<string, number> => state.data.creditCards.activeVcnCount

export const selectCreditCardStatisticsVcnCountFromState = (
  state: RootState,
): Record<string, number> => state.data.creditCards.vcnCount

export const selectCreditCardStatisticsActiveBudgetCountFromState = (
  state: RootState,
): Record<string, number> => state.data.creditCards.activeBudgetCount

export const selectCreditCardStatisticsBudgetCountFromState = (
  state: RootState,
): Record<string, number> => state.data.creditCards.budgetCount

export const selectBudgetUser = (state: RootState): UserListModel | undefined =>
  state.data.creditCards.budgetUser

export const selectBudget = (
  state: RootState,
): CreditCardListModel | undefined => state.data.creditCards.budget

export const selectMostRecentlyCreatedCreditCardListModelFromState =
  createSelector([selectCreditCardListModelsFromState], (creditCards) => {
    return Object.values(creditCards).sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : -1,
    )[0]
  })

export const selectCreditCardStatisticsClearedTransactionDebitAmountFromState =
  (state: RootState): Record<string, number> =>
    state.data.creditCards.clearedTransactionDebitAmount

export const selectCreditCardStatisticsClearedTransactionCreditAmountFromState =
  (state: RootState): Record<string, number> =>
    state.data.creditCards.clearedTransactionCreditAmount

export const selectCreditCardClearedTransactionAmountFromState = createSelector(
  [
    selectCreditCardStatisticsClearedTransactionDebitAmountFromState,
    selectCreditCardStatisticsClearedTransactionCreditAmountFromState,
  ],
  (debitAmount, creditAmount) => {
    return debitAmount !== undefined && creditAmount !== undefined
      ? Object.entries(debitAmount).reduce(
          (acc, curr) => {
            return {
              ...acc,
              [curr[0]]: debitAmount[curr[0]] - creditAmount[curr[0]],
            }
          },
          {} as Record<string, number>,
        )
      : undefined
  },
)
